import TomSelect from 'tom-select';

/**
 * Tom Select instances for site-modal CF7 selects.
 *
 * Keeps the native <select> as form source of truth for Contact Form 7.
 * Empty CF7 `include_blank` options become placeholders, not dropdown items.
 */

/**
 * @return {{ searchPlaceholder: string, searchText: string }}
 */
const getSelectI18n = () => {
	const i18n = window.lecardoVeinModals?.i18n || {};

	return {
		searchPlaceholder: i18n.selectSearchPlaceholder || 'Поиск...',
		searchText: i18n.selectSearchEmpty || 'Ничего не найдено',
	};
};

/**
 * @param {HTMLSelectElement} select
 * @return {string}
 */
const getPlaceholderFromSelect = (select) => {
	const blankOption = Array.from(select.options).find(
		(option) => '' === option.value
	);

	return (
		blankOption?.textContent?.trim() ||
		select.getAttribute('aria-label') ||
		'Select'
	);
};

/**
 * @param {import('tom-select').default} ts
 * @param {string} placeholderText
 * @return {void}
 */
const applyPlaceholder = (ts, placeholderText) => {
	ts.settings.placeholder = placeholderText;

	const placeholderEl = ts.control?.querySelector('.items-placeholder');

	if (placeholderEl instanceof HTMLInputElement) {
		placeholderEl.placeholder = placeholderText;
	}
};

/**
 * Load only real options into Tom Select (skip empty placeholder <option>).
 *
 * @param {import('tom-select').default} ts
 * @param {HTMLSelectElement} select
 * @return {void}
 */
const loadOptionsFromNative = (ts, select) => {
	ts.clear(true);
	ts.clearOptions();

	Array.from(select.options).forEach((option) => {
		if ('' === option.value) {
			return;
		}

		ts.addOption({
			value: option.value,
			text: option.textContent || '',
		});
	});

	ts.refreshOptions(false);
};

/**
 * Rebuild Tom Select options/selection from the native select DOM.
 *
 * Call after CF7/catalog code replaces <option> nodes or changes .value.
 *
 * @param {HTMLSelectElement|null|undefined} select
 * @return {void}
 */
export function syncTomSelectFromNative(select) {
	if (!(select instanceof HTMLSelectElement) || !select.tomselect) {
		return;
	}

	const ts = select.tomselect;
	const selectedValue = select.value;
	const placeholderText = getPlaceholderFromSelect(select);

	applyPlaceholder(ts, placeholderText);
	loadOptionsFromNative(ts, select);

	if (selectedValue) {
		ts.setValue(selectedValue, true);
	} else {
		ts.clear(true);
		select.value = '';
	}
}

/**
 * Enhance a single native select inside a site modal.
 *
 * @param {HTMLSelectElement} select
 * @return {void}
 */
export function enhanceSiteModalSelect(select) {
	if (!(select instanceof HTMLSelectElement) || select.tomselect) {
		return;
	}

	const i18n = getSelectI18n();
	const placeholderText = getPlaceholderFromSelect(select);

	new TomSelect(select, {
		allowEmptyOption: true,
		create: false,
		maxOptions: null,
		maxItems: 1,
		hideSelected: false,
		closeAfterSelect: true,
		placeholder: placeholderText,
		plugins: ['dropdown_input'],
		render: {
			no_results: (_data, escape) =>
				`<div class="no-results">${escape(i18n.searchText)}</div>`,
		},
		onInitialize() {
			applyPlaceholder(this, getPlaceholderFromSelect(select));
			loadOptionsFromNative(this, select);

			if (select.value) {
				this.setValue(select.value, true);
			} else {
				this.clear(true);
				select.value = '';
			}

			const setSearchPlaceholder = () => {
				const searchInput = this.control_input;

				if (searchInput) {
					searchInput.setAttribute('placeholder', i18n.searchPlaceholder);
				}
			};

			setSearchPlaceholder();
			this.on('dropdown_open', setSearchPlaceholder);
		},
	});
}

/**
 * Initialize Tom Select on all `.form-select` fields inside site modals.
 *
 * @return {void}
 */
export function initSiteModalSelects() {
	document
		.querySelectorAll('.site-modal select.form-select')
		.forEach((select) => {
			if (select instanceof HTMLSelectElement) {
				enhanceSiteModalSelect(select);
			}
		});
}
